import { createSlice } from "@reduxjs/toolkit";
import { getPage, getMetadata, getDeletedData } from "./pdf-thunks";
import { checksForCounterUpdate, updateMetadataNode } from "../utils";
import { isNonEmptyArray } from "../../../../common/utils";

const initialState = {
	content: [],
	page: 0,
	zoom: 0,
	position: null,
	scrollTo: 0,
	selectedMenu: 0,
	pagesUpdated: [],
	isScrolling: true,
	firstPage: 0,
	tables: [],
	addRedressmentFocusActive: false,
	reorderRedressmentFocusActive: false,
	reorderContentOrder: null,
	reorderContentDirectionAbove: false,
	reorderDirectByOrderError: false,
};

const srPdfSlice = createSlice({
	name: "srPdf",
	initialState,
	reducers: {
		getReqPageContent: (state, { payload }) => {
			const { page, content } = payload;
			const adjustedPayloads = { ...content };
			const updatedTables = adjustedPayloads?.tables.map((table) => ({
				...table,
				page,
			}));
			return {
				...state,
				reloadingPages: [],
				content: state.content.map((c) =>
					c.page === page ? { ...c, req: adjustedPayloads?.informations } : c
				),
				tables: [...state.tables, ...updatedTables],
			};
		},
		deleteContent: (state, { payload }) => {
			const { pageStart, id } = payload;
			return {
				...state,
				content: state.content.map(
					(pageContent) =>
						(pageContent.page === pageStart && {
							...pageContent,
							redr: pageContent?.redr?.filter((redrContent) => redrContent.contentId !== id),
						}) ||
						pageContent
				),
			};
		},
		addContent: (state, { payload }) => {
			const { pageStart, order } = payload;
			return {
				...state,
				content: state.content.map(
					(pageContent) =>
						(pageContent.page === pageStart && {
							...pageContent,
							redr: [
								...pageContent.redr.filter((content) => content.order < order),
								payload,
								...pageContent.redr.filter((content) => content.order > order),
							],
						}) ||
						pageContent
				),
			};
		},
		setReorderOverlay: (state, { payload }) => ({ ...state, reorderRedressmentFocusActive: payload }),
		setAddRedressmentOverlay: (state, { payload }) => ({ ...state, addRedressmentFocusActive: payload }),
		setReorderIndexDirection: (state, { payload }) => {
			const { rectangle, above } = payload;
			if (!rectangle) {
				return {
					...state,
					reorderRectangles: [],
					reorderContentOrder: null,
					reorderContentDirectionAbove: false,
				};
			}
			const pages = new Set([rectangle.pageStart - 1, rectangle.pageStart, rectangle.pageStart + 1]);
			const rectanglesInPages = state.content
				.filter((c) => pages.has(c.page))
				.flatMap((page) => (page.redr ?? []).map((r) => ({ ...r, page: page.page })))
				.sort((a, b) => a.order - b.order);
			const currentIndex = rectanglesInPages.findIndex((r) => r.order === rectangle.order);
			if (currentIndex === -1) {
				return {
					...state,
					reorderRectangles: [],
					reorderContentOrder: null,
					reorderContentDirectionAbove: false,
					reorderContentTargetOrder: null,
				};
			}
			const swapIndex = above ? currentIndex + 1 : currentIndex - 1;
			const swapper =
				swapIndex >= 0 && swapIndex < rectanglesInPages.length ? rectanglesInPages[swapIndex] : null;
			return {
				...state,
				reorderRectangles: [swapper, rectangle].filter(Boolean),
				reorderContentOrder: rectangle.order,
				reorderContentDirectionAbove: above,
				reorderContentTargetOrder: null,
			};
		},
		setReorderIndexDirectionByOrder: (state, { payload }) => {
			const { rectangle, targetOrder } = payload;
			const pages = new Set([rectangle.pageStart - 1, rectangle.pageStart, rectangle.pageStart + 1]);
			const pageContents = state.content.filter((c) => pages.has(c.page));
			const rectanglesInPages = pageContents
				.flatMap((page) => page.redr.map((r) => ({ ...r, page: page.page })))
				.sort((a, b) => a.order - b.order);
			const currentIndex = rectanglesInPages.findIndex((r) => r.order === rectangle.order);
			const targetIndex = rectanglesInPages.findIndex((r) => r.order === targetOrder);
			if (currentIndex === -1 || targetIndex === -1) {
				return {
					...state,
					reorderRectangles: [],
					reorderContentOrder: null,
					reorderContentDirectionAbove: false,
					reorderContentTargetOrder: null,
				};
			}
			const allReqRectangles = pageContents.flatMap((page) => page.req ?? []);
			const findInfoForOrder = (order) =>
				allReqRectangles.find((info) => info.rectangles.some((r) => r.order === order));
			const currentInfo = findInfoForOrder(rectangle.order);
			const targetInfo = findInfoForOrder(targetOrder);
			const sameInformation = currentInfo && targetInfo && currentInfo.informationId === targetInfo.informationId;
			const bothStandalone = currentInfo?.rectangles.length === 1 && targetInfo?.rectangles.length === 1;
			if (!sameInformation && !bothStandalone) {
				return {
					...state,
					reorderRectangles: [],
					reorderContentOrder: null,
					reorderContentDirectionAbove: false,
					reorderContentTargetOrder: null,
					reorderDirectByOrderError: true,
				};
			}
			const swapper = rectanglesInPages[targetIndex];
			return {
				...state,
				reorderRectangles: [swapper, rectangle].filter(Boolean),
				reorderContentOrder: rectangle.order,
				reorderContentDirectionAbove: targetIndex > currentIndex,
				reorderContentTargetOrder: targetOrder,
			};
		},
		resetReorderError: (state) => ({ ...state, reorderDirectByOrderError: false }),
		swapRedressmentContents: (state, { payload }) => {
			if (!isNonEmptyArray(payload) || !payload.every((r) => !!r.order)) {
				return { ...state };
			}
			const pages = new Set(payload.map((r) => r.pageStart));
			const [first, second] = payload;
			const orderMap = {
				[first.contentId]: second.order,
				[second.contentId]: first.order,
			};
			const swapOrderInRectangles = (rectangles) =>
				rectangles.map((r) => (r.contentId in orderMap ? { ...r, order: orderMap[r.contentId] } : r));

			const swapOrderInItems = (items) =>
				items?.map((item) => ({
					...item,
					rectangles: swapOrderInRectangles(item.rectangles ?? []),
				})) ?? items;
			return {
				...state,
				content: state.content.map((content) => {
					if (!pages.has(content.page)) {
						return content;
					}
					return {
						...content,
						redr: content.redr?.map((r) =>
							r.contentId in orderMap ? { ...r, order: orderMap[r.contentId] } : r
						),
						...(content.req && { req: swapOrderInItems(content.req) }),
						...(content.val && { val: swapOrderInItems(content.val) }),
					};
				}),
				reorderRectangles: [],
				reorderContentOrder: null,
				reorderContentDirectionAbove: false,
			};
		},
		setFirstPage: (state, { payload }) => ({ ...state, firstPage: payload }),
		setIsScrolling: (state, { payload }) => ({ ...state, isScrolling: payload }),
		resetPdf: (state, { payload }) => {
			const { page, position } = payload || {};
			const additional =
				(page &&
					position && {
						page: page - 1,
						position: state.position === position ? position + 0.0001 : position,
						positionPage: page - 1,
						scrollTo: page - 1,
					}) ||
				{};
			return {
				...state,
				...initialState,
				zoom: state.zoom,
				selectedMenu: state.selectedMenu,
				...additional,
			};
		},
		resetContent: (state, { payload }) => ({
			...state,
			reloadingPages: payload.pages,
			hasCounterUpdate: true,
			content: state.content.map((c) =>
				payload.pages.some((x) => x === c.page) ? { ...c, [payload.mode]: [] } : c
			),
		}),
		setPosition: (state, { payload }) => ({ ...state, position: payload }),
		setPage: (state, { payload }) => ({ ...state, page: payload }),
		setScrollTo: (state, { payload }) => {
			const { page, position } = payload;
			if (position) {
				return {
					...state,
					page: page - 1,
					position: state.position === position ? position + 0.0001 : position,
					positionPage: page - 1,
					isScrolling: false,
				};
			}
			return { ...state, page: page - 1, scrollTo: page - 1, isScrolling: false };
		},
		setSelectedMenu: (state, { payload }) => ({ ...state, selectedMenu: payload }),
		setZoom: (state, { payload }) => ({ ...state, zoom: payload }),
		updateNode: (state, { payload }) => ({
			...state,
			content: updateMetadataNode(state.content, payload),
			hasCounterUpdate: checksForCounterUpdate(payload?.updates),
		}),
		setHasCounterUpdate: (state, { payload }) => ({ ...state, hasCounterUpdate: payload }),
		setHasQuickAccessUpdate: (state, { payload }) => ({ ...state, hasQuickAccessUpdate: payload }),
		setHasVersioningUpdate: (state, { payload }) => ({ ...state, hasVersioningUpdate: payload }),
		addPageToPagesUpdated: (state, { payload }) => {
			if (!state.pagesUpdated.includes(payload)) {
				return { ...state, pagesUpdated: [...state.pagesUpdated, payload] };
			}
			return state;
		},
		resetPagesUpdated: (state) => ({ ...state, pagesUpdated: [] }),
	},
	extraReducers: (builder) => {
		builder.addCase(getPage.pending, (state, action) => {
			const { page } = action.meta.arg;
			return {
				...state,
				content: [...state.content, { page, isLoading: true }],
			};
		});
		builder.addCase(getPage.fulfilled, (state, action) => {
			const { page } = action.meta.arg;
			const { payload } = action;
			const blobImage = URL.createObjectURL(payload);
			return {
				...state,
				content: state.content.map((c) =>
					c.page === page ? { ...c, page, image: blobImage, isLoading: false } : c
				),
			};
		});
		builder.addCase(getPage.rejected, (state, action) => {
			const { page } = action.meta.arg;
			return {
				...state,
				content: state.content.map((c) =>
					c.page === page ? { ...c, page, image: null, isLoading: false } : c
				),
			};
		});
		builder.addCase(getMetadata.fulfilled, (state, action) => {
			const { page, mode } = action.meta.arg;
			const { payload } = action;
			let adjustedPayloads = payload;
			if (!Object.keys(payload).includes("informations")) {
				adjustedPayloads = { informations: payload, tables: [] };
			}
			const updatedTables = adjustedPayloads?.tables.map((table) => ({
				...table,
				page,
			}));
			return {
				...state,
				reloadingPages: [],
				content: state.content.map((c) =>
					c.page === page ? { ...c, [mode]: adjustedPayloads?.informations } : c
				),
				tables: [...state.tables, ...updatedTables],
			};
		});
		builder.addCase(getMetadata.rejected, (state) => ({
			...state,
		}));
		builder.addCase(getDeletedData.fulfilled, (state, action) => {
			const { page } = action.meta.arg;
			const { payload } = action;
			const deletedList = payload.map((x) => ({ deletedInfosIds: x.deletedInfosIds, ...x.infoDTO }));
			return {
				...state,
				reloadingPages: [],
				content: state.content.map((c) => (c.page === page ? { ...c, deletedData: deletedList } : c)),
			};
		});
	},
});

export { getPage, getMetadata, getDeletedData };
export const {
	resetReorderError,
	setReorderIndexDirectionByOrder,
	swapRedressmentContents,
	getReqPageContent,
	addContent,
	deleteContent,
	setReorderOverlay,
	setReorderIndexDirection,
	setAddRedressmentOverlay,
	setFirstPage,
	resetContent,
	resetPdf,
	setHasCounterUpdate,
	setHasQuickAccessUpdate,
	setHasVersioningUpdate,
	addPageToPagesUpdated,
	resetPagesUpdated,
	setPage,
	setScrollTo,
	setSelectedMenu,
	setZoom,
	updateNode,
	setPosition,
	setIsScrolling,
} = srPdfSlice.actions;
export default srPdfSlice.reducer;
